import type { JSX } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface StatDelta {
  /** Variation déjà formatée, avec son signe (« +12,4 % »). */
  value: string;
  /** Sens de la flèche. */
  direction: 'up' | 'down';
  /** Teinte selon le sens métier (une baisse de coût est bonne) : `success-fg` ou `danger-fg`. */
  tone: 'good' | 'bad';
  /** Période de comparaison (« vs septembre »). */
  period: string;
}

interface StatCardProps {
  /** Libellé de 1 à 4 mots. */
  label: ReactNode;
  /** Valeur déjà formatée (montant arrondi à l'euro au-delà de 100 €). */
  value: ReactNode;
  /** Pied (`stat-foot`) : contexte, période, détail. */
  foot?: ReactNode;
  /** Pied en mono (code technique). */
  footMono?: boolean;
  delta?: StatDelta;
  className?: string;
}

/**
 * Carte indicateur (components/StatCard, `card stat` des maquettes) : libellé
 * 12px, valeur `figure-lg` mono 28px, pied 12px. Quatre au plus en tête de
 * page, dans un `<Row4>`.
 */
function StatCard({
  label,
  value,
  foot,
  footMono = false,
  delta,
  className,
}: StatCardProps): JSX.Element {
  return (
    <Card className={cn('grid content-start gap-2 p-4', className)}>
      <div className="text-label text-muted-foreground">{label}</div>
      <div className="truncate font-mono text-figure-lg tabular-nums">{value}</div>
      {foot !== undefined || delta !== undefined ? (
        <div
          className={cn(
            'flex flex-wrap items-center gap-2 text-caption text-muted-foreground',
            footMono && 'font-mono',
          )}
        >
          {delta !== undefined ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 font-mono text-xs font-medium',
                delta.tone === 'good' ? 'text-success-fg' : 'text-danger-fg',
              )}
            >
              {delta.direction === 'up' ? (
                <ArrowUpRight className="size-3" aria-hidden="true" />
              ) : (
                <ArrowDownRight className="size-3" aria-hidden="true" />
              )}
              {delta.value}
            </span>
          ) : null}
          {delta !== undefined ? <span>{delta.period}</span> : null}
          {foot !== undefined ? <span className="min-w-0">{foot}</span> : null}
        </div>
      ) : null}
    </Card>
  );
}

export { StatCard };
